/**
 * <swiper-init> — Custom Element that auto-initializes a native Swiper 12
 * instance on connectedCallback. Reads config from `data-swiper-settings`
 * JSON either on `this` (element itself is the Swiper root) or on the first
 * `[data-swiper-settings]` child. Optional thumb swiper via
 * `data-swiper-thumb="<selector>"` + `data-swiper-thumb-settings='<json>'`
 * on the thumb element.
 *
 * Usage in liquid:
 *   <swiper-init class="swiper" data-swiper-settings='{{ swiper_options }}'>
 *     <div class="swiper-wrapper">...</div>
 *   </swiper-init>
 *
 * Public API:
 *   element.swiper         — native Swiper 12 instance (set by Swiper itself
 *                            on `mount()`; read after `swiper:initialized`)
 *   element.thumbSwiper    — native thumb Swiper instance (if any)
 *
 * Dispatched events (bubbles):
 *   swiper:initialized  → { swiper, thumbs }
 *   swiper:destroyed
 *   swiper:slide-changed → { currentIndex, element }
 *
 * Loaded as ES module via `@theme/swiper-init` importmap entry; also loaded
 * globally from `layout/theme.liquid` so every page has the CE defined.
 *
 * Swiper itself is imported directly as an ES module — it is only fetched
 * on pages that actually use a `<swiper-init>` element (lazy load).
 */

import Swiper from '@theme/swiper';

// "Feel" defaults merged UNDER each carousel's own config (per-carousel JSON wins).
// Centralizes the post-release glide + input tuning so every <swiper-init> feels
// consistent without editing each section. Override any key in data-swiper-settings.
const FEEL_DEFAULTS = {
  speed: 450, // gentler glide on snap than Swiper's default 300ms
  grabCursor: true, // desktop drag affordance (no-op on touch)
  touchReleaseOnEdges: true, // hand vertical page scroll back at the first/last slide
  longSwipesRatio: 0.35, // a light flick advances a slide (default 0.5)
};

class SwiperInit extends HTMLElement {
  thumbSwiper = null;
  #rootEl = null;
  #thumbEl = null;

  connectedCallback() {
    this.#rootEl = this.hasAttribute('data-swiper-settings')
      ? this
      : this.querySelector('[data-swiper-settings]');

    if (!this.#rootEl) {
      console.warn('<swiper-init>: no [data-swiper-settings] found', this);
      return;
    }

    const thumbSelector = this.getAttribute('data-swiper-thumb');
    if (thumbSelector) {
      this.#thumbEl = document.querySelector(thumbSelector);
    }

    // Defer init so sibling nav controls (e.g. FWM prev/next after </swiper-init>) exist in DOM.
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#init();
    });
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #init() {
    let config;
    try {
      config = JSON.parse(this.#rootEl.getAttribute('data-swiper-settings'));
    } catch (e) {
      console.error('<swiper-init>: invalid data-swiper-settings JSON', e);
      return;
    }

    config = { ...FEEL_DEFAULTS, ...config };

    if (this.#thumbEl) {
      const thumbConfig = JSON.parse(
        this.#thumbEl.getAttribute('data-swiper-thumb-settings') || '{}'
      );
      this.thumbSwiper = new Swiper(this.#thumbEl, thumbConfig);
      config.thumbs = { ...(config.thumbs || {}), swiper: this.thumbSwiper };
    }

    // Swiper sets `this.#rootEl.swiper = <instance>` internally on mount().
    // When #rootEl === this (the Custom Element), that means `this.swiper`
    // becomes the native Swiper instance automatically.
    const swiper = new Swiper(this.#rootEl, config);

    swiper.on('slideChange', () => {
      this.dispatchEvent(
        new CustomEvent('swiper:slide-changed', {
          bubbles: true,
          detail: { currentIndex: swiper.activeIndex, element: swiper.el },
        })
      );
    });

    this.dispatchEvent(
      new CustomEvent('swiper:initialized', {
        bubbles: true,
        detail: { swiper, thumbs: this.thumbSwiper },
      })
    );
    this.swiper = swiper;
  }

  #destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
      this.thumbSwiper = null;
    }
    this.dispatchEvent(new CustomEvent('swiper:destroyed', { bubbles: true }));
  }
 
}

if (!customElements.get('swiper-init')) {
  customElements.define('swiper-init', SwiperInit);
}
